import { useSetPageTitle } from 'app/shared/context-hooks/use-titlebar.hook';
import styled from 'styled-components/macro';
import {
  DiagnosticsQuote,
  HistoryData,
  RepairQuote,
} from 'app/core/api/user/api-user.types';
import Heading from 'app/shared/components/typography/heading/Heading';
import styledFuncs from 'app/styles/styledFuncs';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@material-ui/core';
import {
  useGetTransactionDetail,
  useGetTransactionHistory,
} from 'app/core/api/user/api-user';
import AppImage from 'app/shared/components/app-image/AppImage';
import { DefaultUserImg } from 'app/shared/components/misc';
import { useWatchAsRef } from 'app/tools/hooks/misc-hooks';
import { useEffect } from 'react';

const LinkHeader = styled(Heading)`
  color: ${p => p.theme.palette.primary.main};
  text-align: left !important;
  font-size: ${styledFuncs.pxToRem(16)};
  margin-left: ${styledFuncs.pad(0.5)};
  font-weight: normal;
`;
const InfoBox = styled.div`
  margin-left: ${styledFuncs.pad(0.5)};
  margin-right: ${styledFuncs.pad(0.5)};
`;
const PriceRightButton = styled.div`
  position: absolute;
  right: ${styledFuncs.pad(2)};
  top: ${styledFuncs.pad(4)};
  font-size: 20px;
  color: #31ad31;
  font-weight: normal;
`;

const StyledAccordion = styled(Accordion)`
  border-radius: 10px;
`;

const CenteredBox = styled.div`
  flex-grow: 1;
  flex-direction: column;
  display: flex;
  justify-content: center;
  text-align: center;
  > * {
    margin: ${styledFuncs.pad(0.5)} 0;
  }
`;

const Table = styled.table`
  border-collapse: separate;
  table-layout: fixed;
  th {
    text-align: left;
    padding: ${styledFuncs.pad(0.5)} ${styledFuncs.pad(1.5)};
  }
  th:first-child {
    width: 50%;
  }
  th:nth-child(2) {
    text-align: center;
    width: 20%;
  }
  th:nth-child(3) {
    text-align: right;
    width: 30%;
  }
  tr {
    border-radius: 10px;
  }
  td {
    border-top: 1px solid #eaeaea;
    border-bottom: 1px solid #eaeaea;
    padding: ${styledFuncs.pad(0.5)} ${styledFuncs.pad(1.5)};
    color: #000;
  }
  td:first-child {
    border-left: 1px solid #eaeaea;
    width: 50%;
    border-radius: 10px 0 0 10px;
  }
  td:nth-child(2) {
    text-align: center;
    width: 20%;
    border-right: solid 1px #fff !important;
  }
  td:last-child {
    border-right: 1px solid #eaeaea;
    border-radius: 0 10px 10px 0;
    text-align: right;
    width: 30%;
  }
  tr:last-child td {
    color: #31ad31;
  }
  thead {
    font-weight: 600;
    display: table;
    table-layout: fixed;
    width: 100%;
  }
  tbody {
    display: table;
    table-layout: fixed;
    width: 100%;
    border-spacing: 0 10px;
  }
`;

export default function History(): JSX.Element {
  useSetPageTitle('History');

  const {
    resource: historyData,
    loading,
    request,
  } = useGetTransactionHistory();

  const { resource: consumerTransaction, request: loadTransaction$ } =
    useGetTransactionDetail();

  const DirectToDetails = (id: number) => {
    loadTransaction$(id);
  };
  const loadingRef = useWatchAsRef(loading);
  useEffect(() => {
    if (!loadingRef.current) request();
  }, [loadingRef, request]);
  const foundTransaction = (
    <>
      <LinkHeader>Recent Transactions</LinkHeader>
      {historyData &&
        historyData.map((val: HistoryData) => (
          <StyledAccordion color="default" key={val.id}>
            <AccordionSummary onClick={() => DirectToDetails(val.id)}>
              <AppImage
                alt="profile image"
                src={val.imageUrl}
                fallbackImgNode={<DefaultUserImg size={60} />}
                height="60px"
                noPad
                className="overflow-hidden w-10 h-10 rounded shrink-0"
              />
              <PriceRightButton>£{val.amount}</PriceRightButton>
              <InfoBox>
                <text style={{ fontWeight: 500, color: '#000' }}>
                  {val.productName}
                </text>
                <br />
                <text>{val.type}</text>
                <br />
                <text>{val.repairerName}</text>
                <br />
                <text>{val.date} &nbsp; &nbsp;</text>
                <text>{val.time}</text>
              </InfoBox>
            </AccordionSummary>
            <AccordionDetails>
              <Table>
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Qty</th>
                    <th>Price</th>
                  </tr>
                </thead>
                <tbody>
                  {consumerTransaction &&
                    consumerTransaction[0]?.diagnosisQuote.map(
                      (dq: DiagnosticsQuote) => (
                        <tr key={dq.quotationAmount.amount}>
                          <td>Diagnosis Quote</td>
                          <td>
                            {
                              consumerTransaction[0]?.diagnosisQuote
                                .length
                            }
                          </td>
                          <td>£{dq.quotationAmount.amount}</td>
                        </tr>
                      )
                    )}
                  {consumerTransaction &&
                    consumerTransaction[0]?.repairQuote.map(
                      (rq: RepairQuote) => (
                        <tr key={rq.quotationAmount.amount}>
                          <td>{val.repairCaseDescription}</td>
                          <td>
                            {
                              consumerTransaction[0]?.repairQuote
                                .length
                            }
                          </td>
                          <td>£{rq.quotationAmount.amount}</td>
                        </tr>
                      )
                    )}
                  <tr>
                    <td>Total</td>
                    <td />
                    <td>£{val.amount}</td>
                  </tr>
                </tbody>
              </Table>
            </AccordionDetails>
          </StyledAccordion>
        ))}
    </>
  );

  const noTransactions = (
    <CenteredBox>
      <text>
        Nothing to see here yet - this is where you'll find
        transactions with repairers when you've completed a payment
      </text>
    </CenteredBox>
  );
  return (
    <>{historyData?.length ? foundTransaction : noTransactions}</>
  );
}
